import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import * as api from "../api";
import { ApiError } from "../api";
import * as I from "../components/icons";
import Vizualizator, { tintaToParam, type Tinta } from "../viewer/Vizualizator";
import {
  filterLine, groupSources, instMeasured, instRequired, normParts, ruleBalance,
  ruleTally, instanceKey, ruleShare, scopeLine, sortInstances, statusChips,
  numeleVerdictului, toggleStatus,
} from "../viewer/validation";
import type {
  RuleBalance, RuleGroup, SourceGroup, ValidationReport, VInstance, VNormLink,
  VRule, VStatus,
} from "../viewer/validation";
import { useT, useTn } from "../i18n";
import type { SolutieRand } from "../nav/api";
import { caleRand, caleVerificari, numeRand, picateGlobaleCorp } from "../nav/selectie";
import { useFir } from "../nav/useFir";
import { EroareRegistru, EroareSolutie } from "../nav/BlocSolutie";
import { useRegistruOptional } from "../nav/Registru";

// Verificările: regulile arhitectului, rulate pe arhitectura din cale
// (`/p/:id/verificari/:sol`) — pe firul pe care stai, nu pe cea promovată.
//
// Se rulează LIVE, la intrarea în ecran, și nu se salvează nimic — validatorul
// nu ține stare, iar raportul se re-derivă din document la fiecare cerere. Pe
// viitor verificarea va porni singură după generare, ca raportul să fie gata
// când omul ajunge aici; ruta e deja aceeași, deci ecranul nu se schimbă atunci.
//
// DOUĂ CERERI, nu una, și diferența e de megabytes: prima desenare cere numai
// verdictele (`instances=0`, ~30 KB dintr-un raport care cu instanțe trece de
// 5 MB pe un proiect de 560 de apartamente), iar instanțele unei reguli se cer
// când i se deschide rândul (`rules=<id>`, o singură regulă re-rulată).

const TON: Record<VStatus, string> = {
  neconform: "bad",
  necesita_confirmare: "amb",
  conform: "ok",
  inaplicabil: "mut",
  neverificabil: "out",
};

function cerere(projectId: string, solutionId: string, qs: string) {
  return api.post<ValidationReport>(
    `/api/projects/${projectId}/solutions/${solutionId}/verificare${qs}`);
}

function VerificariArhitectura({ taburi, solutie }:
                              { taburi?: React.ReactNode; solutie: SolutieRand | null }) {
  const { projectId = "" } = useParams();
  const t = useT();
  const tn = useTn();
  const [raport, setRaport] = useState<ValidationReport | null>(null);
  const faraSolutie = !solutie;
  const [eroare, setEroare] = useState("");
  const [lucreaza, setLucreaza] = useState(true);

  // Filtrul din capul paginii: un set gol înseamnă „fără filtru", iar apăsarea
  // ultimei pastile active îl golește — controlul se desface cu același gest cu
  // care s-a pus.
  const [doar, setDoar] = useState<ReadonlySet<VStatus>>(new Set());
  const [docSel, setDocSel] = useState("");
  const [deschisa, setDeschisa] = useState("");
  const [instante, setInstante] = useState<Record<string, VInstance[]>>({});

  const ruleaza = useCallback(async () => {
    setLucreaza(true);
    setEroare("");
    try {
      if (!solutie) return;
      // Instanțele se lasă deoparte: se cer per regulă, la deschiderea rândului.
      setInstante({});
      setRaport(await cerere(projectId, solutie.id, "?instances=0"));
    } catch (err) {
      setEroare(t(err instanceof ApiError && err.status === 503
        ? "verificari.serviciu_mut" : "verificari.esec"));
    } finally {
      setLucreaza(false);
    }
  }, [projectId, t, solutie]);

  useEffect(() => { ruleaza().catch(() => undefined); }, [ruleaza]);

  // Instanțele rândului deschis, cerute de un EFECT și nu de handlerul de click.
  // Diferența se vede la «Rulează din nou»: raportul nou golește cache-ul, iar un
  // rând rămas deschis ar fi așteptat pe veci o cerere pe care numai un click ar
  // mai fi pornit-o. Aici, „deschis și fără instanțe" e chiar condiția care le
  // cere, oricine a golit cache-ul.
  useEffect(() => {
    if (!deschisa || !solutie || instante[deschisa]) return;
    let renunta = false;
    cerere(projectId, solutie.id, `?rules=${encodeURIComponent(deschisa)}`)
      .then((raspuns) => {
        if (renunta) return;
        setInstante((cur) => ({ ...cur, [deschisa]: raspuns.rules[0]?.instances ?? [] }));
      })
      // Rândul rămâne deschis cu tally-ul lui: o regulă a cărei listă de instanțe
      // n-a venit e altceva decât o regulă fără instanțe, iar un rând care se
      // închide singur arată ca un buton stricat.
      .catch(() => {
        if (!renunta) setEroare(t("verificari.esec_elemente"));
      });
    return () => { renunta = true; };
  }, [deschisa, solutie, instante, projectId, t]);

  const pastile = statusChips(raport);
  const documente = useMemo(() => groupSources(raport, doar), [raport, doar]);
  const activ: SourceGroup | undefined =
    documente.find((d) => d.id === docSel) ?? documente[0];
  const arate = documente.reduce((n, d) => n + d.rules, 0);


  if (faraSolutie) {
    return (
      <div className="page verificari">
        {taburi}
        <div className="page-head">
          <div className="disp">{t("verificari.titlu")}</div>
        </div>
        <p className="note">
          {t("verificari.fara_solutie")}
          <Link to={`/p/${projectId}/solutii`}>
            {t("ansamblu.titlu")}
          </Link>.
        </p>
      </div>
    );
  }

  // Capitolul regulii deschise: firimiturile de deasupra detaliului spun unde
  // ești, iar „unde" are două niveluri — categoria (documentul arhitectului) și
  // subcategoria (capitolul lui). Fără al doilea, un ecran cu șapte capitole sub
  // aceeași categorie arată la fel în toate șapte.
  const capitolDeschis: RuleGroup | undefined = deschisa
    ? activ?.chapters.find((c) => c.rules.some((r) => r.id === deschisa))
    : undefined;
  const reguleDeschisa: VRule | undefined =
    capitolDeschis?.rules.find((r) => r.id === deschisa);

  return (
    <div className="page verificari">
      {taburi}
      {eroare && <p className="note bad">{eroare}</p>}
      {lucreaza && !raport && (
        <p className="note">{t("verificari.se_ruleaza")}</p>
      )}

      {raport && (
        <div className="ver-doua">
          {/* --- Stânga: cine ești, cum stai, ce categorii ------------------ */}
          <div className="ver-stanga">
            <div className="page-head">
              <div className="disp">{t("verificari.titlu")}</div>
              {solutie && <span className="chip c-acc">{solutie.label} ★</span>}
            </div>

            <div className="card ver-sumar">
              <Cadran raport={raport} />
              <div className="ver-pastile">
                {pastile.map((p) => (
                  <button key={p.status} type="button"
                          className={`chip c-${TON[p.status]} ${doar.has(p.status) ? "on" : ""}`}
                          title={t("verificari.arata_numai",
                                   { stare: numeleVerdictului(p.status).toLowerCase() })}
                          onClick={() => setDoar(toggleStatus(doar, p.status))}>
                    {p.label}
                  </button>
                ))}
                {/* Rândul de jos, într-o cutie de înălțime REZERVATĂ.
                    Conținutul comută — linia de scop are două rânduri, cea de
                    filtrare unul — iar fără spațiu rezervat cardul se strângea la
                    fiecare filtrare și trăgea cadranul în sus. Rezervarea costă
                    un rând gol cât timp filtrezi, adică într-o stare trecătoare;
                    alternativa costa o săritură la fiecare apăsare de pastilă.

                    Butonul „arată tot" a plecat: pastila care a pus filtrul îl
                    scoate la a doua apăsare, iar un al doilea control pentru
                    același gest e un al doilea lucru de învățat. */}
                <div className="ver-sublinie">
                  {doar.size > 0 ? (
                    <span className="note ver-filtrat">
                      <I.Filtru size={12} />{filterLine(doar, arate)}
                    </span>
                  ) : (
                    <span className="note ver-scop">{scopeLine(raport)}</span>
                  )}
                </div>
              </div>
            </div>

            <div className="card ver-cat">
              {/* Numele listei e cel din machetă. „Documentele arhitectului" era
                  exact ce SUNT — un document per categorie — dar nu ce cauți
                  aici: te uiți după subiectul verificat, nu după fișier. */}
              <div className="lbl ver-cat-cap">{t("verificari.categorii")}</div>
              {documente.map((d) => (
                <button key={d.id} type="button"
                        className={`rowline ${activ?.id === d.id ? "on" : ""}`}
                        onClick={() => { setDocSel(d.id); setDeschisa(""); }}>
                  <span className="sp" style={{ textAlign: "left" }}>{d.title}</span>
                  {d.failures > 0
                    ? <span className={`chip c-${TON.neconform}`}>{d.failures}</span>
                    : <span className={`chip c-${TON[d.status]}`}>
                        {numeleVerdictului(d.status)}
                      </span>}
                </button>
              ))}
              {!documente.length && (
                <p className="note ver-gol">{t("verificari.nimic_sub_filtru")}</p>
              )}
            </div>
          </div>

          {/* --- Dreapta: unde ești, apoi regulile ------------------------- */}
          <div className="ver-dreapta">
            {activ && (
              <div className="ver-firimituri">
                {reguleDeschisa ? (
                  <button type="button" className="ver-cat-nume ver-inapoi"
                          title={t("verificari.inapoi")}
                          onClick={() => setDeschisa("")}>
                    {activ.title}
                  </button>
                ) : (
                  <span className="ver-cat-nume">{activ.title}</span>
                )}
                <span className="ver-slash">/</span>
                {capitolDeschis ? (
                  <b>{capitolDeschis.chapterId} · {capitolDeschis.chapterTitle}</b>
                ) : (
                  <span>
                    {tn("verificari.capitole#n", activ.chapters.length)}
                    {" · "}{tn("verificari.reguli#n", activ.rules)}
                  </span>
                )}
                <div className="sp" />
                {capitolDeschis?.norm && (
                  <span className="chip c-mut" title={t("verificari.norma_capitolului")}>
                    {capitolDeschis.norm}
                  </span>
                )}
              </div>
            )}

            {/* O regulă deschisă ține TOT panoul, ca în machetă: detaliul are un
                tabel de elemente și un bilanț, iar înghesuit între rândurile
                surorilor lui nu se citește niciunul. Lista se întoarce de la
                firimituri. */}
            <div className="ver-reguli">
              {reguleDeschisa ? (
                <Regula regula={reguleDeschisa} deschisa instante={instante[reguleDeschisa.id]}
                        onClick={() => setDeschisa("")}
                        projectId={projectId} solutie={solutie} />
              ) : activ?.chapters.map((cap) => (
                /* Un card per capitol, cu titlul drept cap al lui. Titlurile
                   pluteau deasupra unui șir de carduri egale, deci nu se vedea
                   unde se termină un capitol și începe următorul — nici cărui
                   capitol aparține prima regulă de după titlu. */
                <section className="card ver-capitol"
                         key={`${cap.sourceId}/${cap.chapterId}`}>
                  <h2 className="ver-capitol-cap">
                    <span className="ver-capitol-nr">{cap.chapterId}</span>
                    {cap.chapterTitle}
                    {cap.norm && (
                      <span className="note"> · <Norma text={cap.norm}
                                                       links={cap.normLinks} /></span>
                    )}
                  </h2>
                  {cap.rules.map((r) => (
                    <Regula key={r.id} regula={r} deschisa={false}
                            instante={instante[r.id]}
                            onClick={() => setDeschisa(r.id)}
                            projectId={projectId} solutie={solutie} />
                  ))}
                </section>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/** Citarea unei norme, cu actele care se pot citi făcute linkuri PE LOC.
 *
 *  „Legea 114/1996, anexa nr. 1, secț. F" — numai numele actului e link, restul
 *  rămâne text: anexa și secțiunea nu au pagină proprie pe portal, iar un link
 *  peste tot șirul ar promite că duce fix la secțiunea F.
 *
 *  Actele nelegate nu se semnalează în niciun fel. NP 057-02 și NP 068-02 sunt
 *  reglementări tehnice din 2002 publicate în Buletinul Construcțiilor, nu în
 *  portalul legislativ; un „(fără link)" lângă ele ar fi de trei ori pe ecran o
 *  informație despre noi, nu despre normă. */
function Norma({ text, links }: { text: string; links?: VNormLink[] }) {
  const t = useT();
  const parts = normParts(text, links);
  return (
    <>
      {parts.map((p, k) => p.url ? (
        /* `rel="noreferrer"` și nu doar `noopener`: portalul legislativ n-are de
           ce să afle din ce ecran a venit cineva. */
        <a key={k} href={p.url} target="_blank" rel="noreferrer"
           className={`ver-norma-link ${p.isPdf ? "pdf" : ""}`}
           /* Un PDF de 11 MB care pornește la un click, dintr-o linie care arată
              ca orice altă trimitere, e un link care se poartă urât chiar dacă
              duce exact unde trebuie. Deci o spune înainte, și în titlu, și
              printr-un semn. */
           title={t(p.isPdf ? "verificari.pdf_titlu" : "verificari.link_titlu",
                    { act: p.title ?? "" })}
           onClick={(e) => e.stopPropagation()}>
          {p.text}{p.isPdf && <span className="ver-norma-pdf" aria-label="PDF"> ⤓</span>}
        </a>
      ) : <span key={k}>{p.text}</span>)}
    </>
  );
}

/** Cât din ce s-a PUTUT măsura e conform.
 *
 *  Numitorul e conform + neconform, nu tot registrul: o regulă `inaplicabil` are
 *  DACĂ-ul fals aici, iar una `neverificabil` n-a fost măsurată de nimeni.
 *  Numărate ca reușite, ar da un procent care crește cu fiecare regulă pe care
 *  modelul nu o poate încă verifica — exact felul în care un validator lingușește
 *  un desen. De asta procentul își spune și numitorul, dedesubt. */
function Cadran({ raport }: { raport: ValidationReport }) {
  const t = useT();
  const { procent, conform, total, decise } = ruleShare(raport);
  // 62 px, nu 76: cadranul stă acum LÂNGĂ pastile, nu deasupra lor, iar fiecare
  // pixel al lui e un pixel pe care pastilele nu-l au pentru a încăpea două pe
  // rând.
  const R = 24;
  const circumferinta = 2 * Math.PI * R;
  const plin = (procent / 100) * circumferinta;
  // Trei cifre, despărțite de puncte, fără nicio propoziție explicativă.
  //
  // Varianta de dinainte încerca să EXPLICE de ce o regulă n-a fost măsurată
  // („regula nu se aplică aici, sau modelul nu are ce măsura") — o frază lungă,
  // cu un „sau" care nu spunea care dintre cele două, pusă acolo unde se
  // așteaptă un număr. Statusurile au deja nume, iar numele lor sunt și pe
  // pastilele de lângă cadran: aici e de ajuns cifra pe care pastilele nu o
  // spun — câte s-au putut măsura — și totalul.
  const explicat = [
    t("verificari.cadran_conforme", { conforme: conform, total }),
    t("verificari.cadran_masurate", { masurate: decise, total }),
    t("verificari.cadran_neverificabile",
      { n: raport.summary.neverificabil, total }),
  ].join("\n");
  return (
    <div className="ver-cadran" data-tip={explicat} data-tip-jos="">
      <svg width="62" height="62" viewBox="0 0 62 62">
        <circle cx="31" cy="31" r={R} fill="none" stroke="#e7e7e7" strokeWidth="7.5" />
        <circle cx="31" cy="31" r={R} fill="none" stroke="var(--ok)" strokeWidth="7.5"
                strokeLinecap="round"
                strokeDasharray={`${plin} ${circumferinta - plin}`}
                transform="rotate(-90 31 31)" />
        <text x="31" y="35" textAnchor="middle" fontSize="14" fontWeight="600"
              fill="currentColor">{procent} %</text>
      </svg>
    </div>
  );
}

function Regula({ regula, deschisa, instante, onClick, projectId, solutie }: {
  regula: VRule; deschisa: boolean; instante?: VInstance[]; onClick: () => void;
  projectId: string; solutie: SolutieRand | null;
}) {
  const bilant = ruleBalance(regula);
  const nav = useNavigate();
  const t = useT();
  const tn = useTn();
  // Elementul previzualizat: cel apăsat în listă, altfel primul — lista e
  // sortată cu neconformele sus, deci implicitul e chiar cel despre care e
  // vorba.
  const [alesul, setAlesul] = useState("");
  // Câte rânduri se desenează. 7020 de rânduri dintr-o dată blochează pagina;
  // pragul se ridică la cerere, din tabel.
  const [limita, setLimita] = useState(50);
  const lista = instante ? sortInstances(instante) : [];
  const ales = lista.find((i) => instanceKey(i) === alesul) ?? lista[0];
  const tinta = tintaInstantei(ales);
  return (
    <div className={`card ver-regula ${deschisa ? "on" : ""}`}>
      <button type="button" className="ver-cap" onClick={onClick}>
        <span className="ref">{regula.code}</span>
        <span className="ver-titlu">
          <b className={deschisa ? "" : "ver-scurt"}>{regula.requirement_ro}</b>
          <span className="note">{ruleTally(regula)}</span>
        </span>
        <span className={`chip c-${TON[regula.status]}`}>{numeleVerdictului(regula.status)}</span>
      </button>

      {deschisa && (
        <div className="ver-detaliu">
          {/* Propoziția arhitectului lângă bilanț: ce cere regula și cât din
              clădire o respectă, citite dintr-o privire. Macheta le pune pe două
              coloane exact pentru asta. */}
          <div className="ver-cutii">
            {/* Propozițiile arhitectului, citate. Textul ăsta e cel semnat, nu o
                parafrază a lui — vezi `validator/README.md`. */}
            <div className="ver-daca">
              <div>
                <span className="lbl">{t("verificari.daca")}</span>
                <div>{regula.condition_ro}</div>
              </div>
              <div>
                <span className="lbl">{t("verificari.atunci")}</span>
                <div>{regula.requirement_ro}</div>
              </div>
              {regula.norm && (
                <div className="note">
                  <Norma text={regula.norm} links={regula.norm_links} />
                </div>
              )}
              {regula.applies_to && (
                <div className="note">
                  {t("verificari.se_aplica_la", { ce: regula.applies_to })}
                </div>
              )}
              {/* Metoda, strânsă, dar ÎN aceeași casetă: e tot textul
                  arhitectului despre aceeași regulă. Afară, își lua un rând al
                  ei între propoziție și tabel, adică spațiu pentru un titlu.
                  `<details>` și nu tooltip: sunt paragrafe, iar un paragraf care
                  dispare când muți mouse-ul nu se poate citi. */}
              {(regula.reason_ro || regula.notes_ro) && (
                <details className="ver-metoda">
                  <summary className="lbl">{t("verificari.detalii")}</summary>
                  {regula.reason_ro && <p className="note">{regula.reason_ro}</p>}
                  {regula.notes_ro && <p className="note">{regula.notes_ro}</p>}
                </details>
              )}
            </div>
            <Bilant bilant={bilant} />
          </div>

          {regula.error && (
            <p className="note bad">{t("verificari.regula_cazuta")}</p>
          )}

          {instante === undefined && (regula.counts?.total ?? 0) > 0 && (
            <p className="note">{t("verificari.se_incarca_elemente")}</p>
          )}
          {instante !== undefined && lista.length > 0 && (
            /* Lista la stânga, previzualizarea la dreapta ei, pe ACELAȘI rând —
               ca în machetă. Sub bilanț, previzualizarea era la 400 px de
               rândul care o conduce: apeși un tronson jos în listă și desenul se
               schimbă în afara câmpului vizual. */
            <div className="ver-rand-lista">
              <div className="ver-lista">
              <div className="ver-tabel-cadru">
                <table className="ver-tabel">
                  <thead>
                    <tr><th>{t("verificari.col_element")}</th>
                      <th>{t("verificari.col_masurat")}</th>
                      <th>{t("verificari.col_prag")}</th>
                      <th className="ver-semn">{t("verificari.col_verdict")}</th>
                      <th></th></tr>
                  </thead>
                  <tbody>
                    {lista.slice(0, limita).map((i) => {
                      const cheie = instanceKey(i);
                      const tintaRand = tintaInstantei(i);
                      return (
                      <tr key={cheie} className={cheie === instanceKey(ales) ? "on" : ""}
                          onClick={() => setAlesul(cheie)}>
                        <td>{i.label}</td>
                        {/* Măsurat și prag în două coloane, ca în machetă: una
                            lângă alta se citesc pe verticală, iar cât de departe
                            e fiecare element se vede fără să citești rândul. */}
                        <td className="mono">{instMeasured(i, regula)}</td>
                        <td className="mono note">{instRequired(i, regula)}</td>
                        {/* Verdictul ca SEMN, cu motivul la hover. Textul lui și
                            o coloană „ce s-a găsit" ocupau împreună jumătate din
                            lățime pentru o informație care, pe 50 de rânduri,
                            se repetă aproape identic. Semnul rămâne citibil
                            dintr-o privire; motivul e la un pas. */}
                        {/* Semnul, cu MOTIVUL la hover: `detail_ro` e propoziția
                            care stătea în coloana din dreapta, adusă pe semn.
                            Verdictul îl spune deja culoarea și forma. */}
                        <td className={`ver-semn c-${TON[i.status]}`}
                            data-tip={`${numeleVerdictului(i.status)} — ${i.detail_ro}`}>
                          {i.status === "conform" ? <I.Bifa size={14} />
                            : i.status === "neconform" ? <I.X size={14} />
                            : <span className="ver-semn-alt" aria-label={numeleVerdictului(i.status)}>·</span>}
                        </td>
                        <td className="ver-pin">
                          {tintaRand ? (
                            <button className="btn btng"
                                    title={t("verificari.vezi_pe_plansa")}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      // `rule` și `inst` duc DESENUL validatorului
                                      // mai departe: Studio cere regula și
                                      // regăsește instanța, ca planul mare să
                                      // arate aceeași cotă roșie ca previzualizarea.
                                      if (!solutie) return;
                                      nav(caleRand(projectId, solutie, {
                                        sel: tintaToParam(tintaRand),
                                        rule: regula.id,
                                        inst: instanceKey(i),
                                      }));
                                    }}>
                              <I.Pin size={14} />
                            </button>
                          ) : null}
                        </td>
                      </tr>);
                    })}
                    {/* Limita există fiindcă 7020 de rânduri într-un tabel
                        blochează pagina la desenare; dar «încă 94» care nu se
                        poate deschide e o listă care ascunde fără să spună cum.
                        Acum spune, și se deschide. */}
                    {lista.length > limita && (
                      <tr className="ver-mai-mult">
                        <td colSpan={5}>
                          <button className="btn btng" onClick={(e) => {
                            e.stopPropagation(); setLimita(lista.length);
                          }}>
                            {tn("verificari.mai_multe#n", lista.length - limita)}
                          </button>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              </div>

              {/* ACELAȘI viewer ca Studio, în mod compact. Un desenator „mic"
                  separat ar fi al doilea loc în care un element își alege
                  aspectul, iar aici desenul e dovada pentru cifra de deasupra. */}
              {solutie && tinta && (
                <div className="ver-previz">
                  <Vizualizator projectId={projectId} solution={solutie}
                                compact tinta={tinta}
                                adnotare={ales?.anno
                                  ? { anno: ales.anno, status: ales.status } : null} />
                  <span className="lbl">
                    {t("verificari.pe_plansa", { element: ales?.label ?? "" })}
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/** Bilanțul pe soluție: cifra mare, numitorul ei și cât de departe e.
 *
 *  Lipsește când regula n-a decis nimic — `ruleBalance` întoarce `null` acolo.
 *  O casetă cu «0 din 0 conforme» ar arăta ca un eșec, iar diferența dintre
 *  „n-a trecut nimic" și „nu s-a măsurat nimic" e chiar cea pe care ecranul ăsta
 *  există ca să n-o ascundă. */
function Bilant({ bilant }: { bilant: RuleBalance | null }) {
  const t = useT();
  if (!bilant) return null;
  const procent = Math.round(bilant.fractie * 100);
  return (
    <div className="ver-bilant">
      <span className="lbl">{t("verificari.bilant")}</span>
      {/* `eticheta` e substantivul regulii («apartamente», «uși»), scris de
          validator în română — v. secțiunea de catalog. */}
      <div className="ver-bilant-cifra">
        <b className="disp">{bilant.conforme}</b>
        {t("verificari.bilant_cifra")}
        <b>{bilant.din}</b>{" "}
        {bilant.eticheta}{t("verificari.bilant_conforme")}
      </div>
      {(bilant.fin || bilant.prag) && (
        <div className="note">
          {[bilant.fin, bilant.prag && t("verificari.pragul", { prag: bilant.prag })]
            .filter(Boolean).join(" · ")}
        </div>
      )}
      {/* Bara e verdele peste roșu, nu invers: partea plină e ce e CONFORM, la
          fel ca procentul din cadranul de sus. Două bare care umplu în direcții
          diferite pe același ecran se citesc greșit o dată din două. */}
      <div className="ver-bara" title={t("verificari.procent_conforme", { procent })}>
        <div style={{ width: `${procent}%` }} />
      </div>
    </div>
  );
}

/** Ținta pentru viewer. Doar instanțele cu ID de document se pot arăta: fără el
 *  n-avem ce căuta în planul de pe ecran, iar indicii din raport arată unde ERA
 *  elementul, nu unde e. Rândurile fără ID rămân fără pin, în loc să ducă la o
 *  selecție greșită. */
function tintaInstantei(i: VInstance | undefined): Tinta | null {
  if (!i) return null;
  const r = i.ref as { kind?: string; id?: string; level?: number };
  return r.id && r.kind && typeof r.level === "number"
    ? { kind: r.kind, level: r.level, id: r.id } : null;
}

// ---------------------------------------------------------------------------
// Verificările RÂNDULUI din cale (contract §8): disciplina o spune rândul —
// arhitectura trece prin validatorul regulilor arhitectului, structura prin
// tabloul verdictelor ei. Tab-urile mută pe rândul celeilalte discipline de pe
// ACELAȘI fir. Fără id, se deschid verificările firului.
// ---------------------------------------------------------------------------

import { Navigate, useSearchParams } from "react-router-dom";
import ObsHost from "../structure/ObsHost";
import VerificariStructura from "../structure/VerificariPage";
import { useStructure } from "../structure/StructureContext";

const DISCIPLINE_VER = [
  { cheie: "architecture", eticheta: "verificari.tab_arhitectura" },
  { cheie: "structure", eticheta: "verificari.tab_structura" },
] as const;

export default function Verificari() {
  const { projectId = "" } = useParams();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const t = useT();
  const structura = useStructure();
  const { ruta, rand, fir, eroare, incarcat } = useFir();
  const registrul = useRegistruOptional();

  // registrul n-a putut fi citit deloc: se spune, nu «Se încarcă…» la nesfârșit
  if (!registrul?.registru && registrul?.eroare) {
    return (
      <div className="page verificari" style={{ position: "relative", minHeight: 200 }}>
        <EroareRegistru motiv={registrul.eroare} />
      </div>
    );
  }
  if (!ruta.sol) {
    if (!incarcat) return <div className="page"><p className="note">{t("comun.se_incarca")}</p></div>;
    const tinta = fir.structure ?? fir.architecture;
    if (tinta) return <Navigate replace to={caleVerificari(projectId, tinta)} />;
    return <VerificariArhitectura solutie={null} />;
  }
  if (eroare) {
    return (
      <div className="page verificari" style={{ position: "relative", minHeight: 200 }}>
        <EroareSolutie fel={eroare}
                       href={caleVerificari(projectId, fir.structure ?? fir.architecture)} />
      </div>
    );
  }
  if (!rand) return <div className="page"><p className="note">{t("comun.se_incarca")}</p></div>;
  const disc = rand.discipline === "structure" ? "structure" : "architecture";

  const taburi = (
    <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "0 0 4px" }}>
      <div className="tabs">
        {DISCIPLINE_VER.map((d) => {
          const id = d.cheie === "structure" ? fir.structure : fir.architecture;
          return (
            <button key={d.cheie} className={disc === d.cheie ? "on" : ""}
                    disabled={!id}
                    onClick={() => { if (id) navigate(caleVerificari(projectId, id)); }}>
              {t(d.eticheta)}
            </button>
          );
        })}
      </div>
      {/* soluția cu mai multe corpuri: verificările structurii sunt ale UNUI
          corp (modurile, driftul, cutia sunt ale fiecărei clădiri), iar
          «ansamblu» poartă infrastructura comună */}
      {disc === "structure" && structura.corpuri.length > 1 && (
        <select className="inp meniu-select" style={{ width: "auto" }}
                aria-label={t("struct.corpuri.aria")}
                value={structura.corpuri.find((c) => c.activ)?.cod ?? ""}
                onChange={(e) => {
                  const q = new URLSearchParams(params);
                  q.set("corp", e.target.value);
                  setParams(q);
                }}>
          {structura.corpuri.map((c) => (
            <option key={c.cod} value={c.cod}>
              {c.ansamblu ? t("struct.corpuri.ansamblu") : c.cod}
              {picateGlobaleCorp(rand, c) ? ` · ${picateGlobaleCorp(rand, c)}` : ""}
            </option>
          ))}
        </select>
      )}
      <span className="note">{numeRand(rand)}</span>
    </div>
  );

  if (disc === "structure") {
    // versiunea pașilor spațiului, din rezumatele lor: tabloul se cere din nou
    // când se mișcă una (serverul răspunde 304 dacă nu e una din ale lui)
    const versiune = Object.entries(structura.rezumate ?? {})
      .map(([k, r]) => `${k}:${r.updated_at ?? ""}`).sort().join("|");
    return (
      // ObsHost turns the board's plain <a href> links into SPA navigations
      // and provides the scroll container; the board itself renders `.page
      // verificari`, the same shell as the architecture side, tab strip
      // included.
      <ObsHost className="ob-main">
        <VerificariStructura id={structura.projectId}
                             solutie={structura.solutie}
                             cloneId={structura.cloneId}
                             versiune={versiune}
                             corp={structura.corpuri.find((c) => c.activ)?.cod ?? null}
                             taburi={taburi} />
      </ObsHost>
    );
  }
  return <VerificariArhitectura taburi={taburi} solutie={rand} key={rand.id} />;
}
